/**
 * Iter 136 · Administration → Users & Access.
 *
 * Owner+Admin only. Lists users, supports invite/edit/suspend/revoke/
 * reactivate/force-logout. All dropdowns are searchable and populated
 * from live directory data (ventures, departments, cost centers,
 * managers, roles).
 *
 * Backend endpoints under `/api/pureh/admin/users-access/*` enforce
 * the role hierarchy (only Owner may grant Owner/Admin; Admin cannot
 * self-promote). This UI mirrors those rules for a friendlier surface
 * but the server remains the source of truth.
 */
import { useEffect, useMemo, useState } from "react";
import { UserPlus, ShieldCheck, X, LogOut, RefreshCw, History } from "lucide-react";
import { usePureH } from "../context/PureHContext";

type Directory = {
  roles: string[];
  statuses: string[];
  ventures: { id: string; name: string }[];
  departments: { key?: string; name: string }[];
  costCenters: { id: string; name: string; code?: string }[];
  managers: { id: string; name: string; email: string; role: string }[];
};

type Row = {
  id: string;
  email: string;
  name: string;
  role: string;
  ventureIds: string[];
  department?: string;
  team?: string;
  jobTitle?: string;
  moduleAccess: string[];
  approvalAuthority: string[];
  approvalLimit?: number | null;
  costCenter?: string;
  directManagerId?: string;
  accessStartDate?: string;
  accessExpiryDate?: string;
  status: string;
  isHoldingOwner?: boolean;
  createdAt?: string;
  activatedAt?: string;
  lastLoginAt?: string;
  failedLoginCount?: number;
};

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
  });
  if (!r.ok) {
    let msg = `HTTP ${r.status}`;
    try { const j = await r.json(); msg = j.detail || msg; } catch { /* noop */ }
    throw new Error(msg);
  }
  return r.json();
}

const MODULE_KEYS = [
  "executive","workspace","product-master","rd","operations","commerce",
  "finance","growth","marketing","launch","investors","people",
  "knowledge","governance","ai","administration",
];

const APPROVAL_TYPES = [
  "payments","cod","expenses","purchase_orders","product_publish",
  "batch_release","budget_change","hiring","access_grant",
];

export default function UsersAccess() {
  const { user } = usePureH();
  const isAdminOrOwner = !!user && (
    (user.role || "").toLowerCase() === "owner" ||
    (user.role || "").toLowerCase() === "admin" ||
    !!(user as any).isHoldingOwner
  );
  const [rows, setRows] = useState<Row[]>([]);
  const [dir, setDir]   = useState<Directory | null>(null);
  const [q, setQ]       = useState("");
  const [filterRole, setFilterRole]     = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [selected, setSelected] = useState<Row | null>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [showInvite, setShowInvite] = useState(false);
  const [err, setErr]   = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    setBusy(true); setErr(null);
    try {
      const [users, directory] = await Promise.all([
        api<Row[]>("/api/pureh/admin/users-access"),
        api<Directory>("/api/pureh/admin/users-access/directory"),
      ]);
      setRows(users);
      setDir(directory);
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };

  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => rows.filter((r) => {
    const ql = q.toLowerCase();
    if (ql && !r.email.toLowerCase().includes(ql) && !r.name.toLowerCase().includes(ql)) return false;
    if (filterRole   && (r.role   || "").toLowerCase() !== filterRole)   return false;
    if (filterStatus && (r.status || "").toLowerCase() !== filterStatus) return false;
    return true;
  }), [rows, q, filterRole, filterStatus]);

  return (
    <div className="space-y-6" data-testid="users-access-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Administration</div>
          <h1 className="text-2xl font-semibold text-slate-900">Users &amp; Access</h1>
          <p className="text-sm text-slate-600 max-w-2xl mt-1">
            Invite Google-authenticated users, assign venture / department / role and manage module permissions.
            Only Owner may grant Owner or Admin privileges. Google sign-in is invitation-only.
          </p>
        </div>
        {isAdminOrOwner && (
          <button
            onClick={() => setShowInvite(true)}
            data-testid="users-invite-btn"
            className="inline-flex items-center gap-1.5 rounded bg-slate-900 text-white px-3 py-1.5 text-sm hover:bg-slate-800"
          >
            <UserPlus size={14} /> Invite user
          </button>
        )}
      </header>

      <div className="flex flex-wrap gap-2 items-center">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search email or name…"
          data-testid="users-search"
          className="flex-1 min-w-[240px] border border-slate-300 rounded px-3 py-1.5 text-sm"
        />
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          data-testid="users-filter-role"
          className="border border-slate-300 rounded px-2 py-1.5 text-sm"
        >
          <option value="">All roles</option>
          {(dir?.roles || []).map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          data-testid="users-filter-status"
          className="border border-slate-300 rounded px-2 py-1.5 text-sm"
        >
          <option value="">All statuses</option>
          {(dir?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={reload} disabled={busy} data-testid="users-refresh" className="text-slate-500 hover:text-slate-800 flex items-center gap-1 text-xs">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {err && <div className="text-sm text-red-600" data-testid="users-error">{err}</div>}

      <div className="border border-slate-200 rounded overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2 text-left">Email</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-left">Ventures</th>
              <th className="px-3 py-2 text-left">Department</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Last login</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr
                key={u.id}
                className="border-t border-slate-200 hover:bg-slate-50 cursor-pointer"
                onClick={() => setSelected(u)}
                data-testid={`users-row-${u.id}`}
              >
                <td className="px-3 py-2 font-medium text-slate-900">{u.email}</td>
                <td className="px-3 py-2">{u.name}</td>
                <td className="px-3 py-2"><RolePill role={u.role} groupOwner={u.isHoldingOwner} /></td>
                <td className="px-3 py-2 text-xs text-slate-600">{(u.ventureIds || []).join(", ") || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{u.department || "—"}</td>
                <td className="px-3 py-2"><StatusPill status={u.status} /></td>
                <td className="px-3 py-2 text-xs text-slate-500">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : "—"}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelected(u); void loadSessions(u.id, setSessions); }}
                    data-testid={`users-sessions-${u.id}`}
                    className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                    title="View login history"
                  >
                    <History size={12} /> History
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && !busy && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500 text-sm">No users match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && dir && (
        <EditPanel
          user={selected}
          dir={dir}
          sessions={sessions}
          onClose={() => { setSelected(null); setSessions([]); }}
          onSaved={reload}
        />
      )}

      {showInvite && dir && (
        <InvitePanel dir={dir} onClose={() => setShowInvite(false)} onSaved={() => { setShowInvite(false); void reload(); }} />
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────

function RolePill({ role, groupOwner }: { role: string; groupOwner?: boolean }) {
  const g = groupOwner ? "bg-amber-100 text-amber-800 border-amber-200"
        : (role === "owner" || role === "admin") ? "bg-slate-900 text-white border-slate-900"
        : "bg-slate-100 text-slate-700 border-slate-200";
  return <span className={`inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${g}`} data-testid={`role-pill-${role}`}>{groupOwner ? "holding-owner" : role}</span>;
}

function StatusPill({ status }: { status: string }) {
  const s = (status || "active").toLowerCase();
  const cls = s === "active" ? "bg-emerald-100 text-emerald-700 border-emerald-200"
            : s === "invited" ? "bg-blue-100 text-blue-700 border-blue-200"
            : s === "suspended" ? "bg-orange-100 text-orange-700 border-orange-200"
            : s === "revoked" || s === "terminated" ? "bg-red-100 text-red-700 border-red-200"
            : "bg-slate-100 text-slate-700 border-slate-200";
  return <span className={`inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${cls}`} data-testid={`status-pill-${s}`}>{s}</span>;
}

async function loadSessions(uid: string, setSessions: (s: any[]) => void) {
  try { setSessions(await api<any[]>(`/api/pureh/admin/users-access/${uid}/sessions`)); }
  catch { setSessions([]); }
}

function EditPanel({ user, dir, sessions, onClose, onSaved }: {
  user: Row; dir: Directory; sessions: any[];
  onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState<Row>(user);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setForm(user); }, [user.id]);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await api(`/api/pureh/admin/users-access/${user.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          role: form.role,
          ventureIds: form.ventureIds,
          department: form.department || null,
          team: form.team || null,
          jobTitle: form.jobTitle || null,
          moduleAccess: form.moduleAccess,
          approvalAuthority: form.approvalAuthority,
          approvalLimit: form.approvalLimit ?? null,
          costCenter: form.costCenter || null,
          directManagerId: form.directManagerId || null,
          accessStartDate: form.accessStartDate || null,
          accessExpiryDate: form.accessExpiryDate || null,
          status: form.status,
        }),
      });
      onSaved();
      onClose();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const changeStatus = (s: string) => setForm({ ...form, status: s });

  const forceLogout = async () => {
    if (!confirm("Force-logout all active sessions for this user?")) return;
    try { await api(`/api/pureh/admin/users-access/${user.id}/force-logout`, { method: "POST" }); onSaved(); }
    catch (e: any) { alert(e.message); }
  };

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="users-edit-panel">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-[520px] bg-white border-l border-slate-200 h-full overflow-y-auto p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Edit user</div>
            <div className="text-lg font-semibold text-slate-900">{user.email}</div>
          </div>
          <button onClick={onClose} data-testid="edit-close"><X size={16} /></button>
        </div>

        <Field label="Full name"><input value={form.name} readOnly className="w-full border border-slate-200 rounded px-2 py-1 text-sm bg-slate-50" /></Field>
        <Field label="Role">
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="edit-role" className="w-full border border-slate-300 rounded px-2 py-1 text-sm">
            {dir.roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <div className="flex flex-wrap gap-1.5" data-testid="edit-status-row">
            {dir.statuses.map((s) => (
              <button
                key={s}
                onClick={() => changeStatus(s)}
                data-testid={`edit-status-${s}`}
                className={
                  "text-[11px] uppercase tracking-wide px-2 py-1 rounded border " +
                  (form.status === s
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50")
                }
              >
                {s}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Ventures">
          <MultiSelect
            options={dir.ventures.map((v) => ({ value: v.id, label: v.name }))}
            value={form.ventureIds}
            onChange={(v) => setForm({ ...form, ventureIds: v })}
            testId="edit-ventures"
          />
        </Field>
        <Field label="Department">
          <select value={form.department || ""} onChange={(e) => setForm({ ...form, department: e.target.value || undefined })} data-testid="edit-dept" className="w-full border border-slate-300 rounded px-2 py-1 text-sm">
            <option value="">—</option>
            {dir.departments.map((d) => <option key={d.key || d.name} value={d.name}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="Team"><input value={form.team || ""} onChange={(e) => setForm({ ...form, team: e.target.value })} data-testid="edit-team" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
        <Field label="Job title"><input value={form.jobTitle || ""} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} data-testid="edit-title" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
        <Field label="Module access">
          <MultiSelect
            options={MODULE_KEYS.map((k) => ({ value: k, label: k }))}
            value={form.moduleAccess}
            onChange={(v) => setForm({ ...form, moduleAccess: v })}
            testId="edit-modules"
          />
        </Field>
        <Field label="Approval authority">
          <MultiSelect
            options={APPROVAL_TYPES.map((k) => ({ value: k, label: k }))}
            value={form.approvalAuthority}
            onChange={(v) => setForm({ ...form, approvalAuthority: v })}
            testId="edit-approvals"
          />
        </Field>
        <Field label="Approval limit"><input type="number" value={form.approvalLimit ?? ""} onChange={(e) => setForm({ ...form, approvalLimit: e.target.value === "" ? null : Number(e.target.value) })} data-testid="edit-limit" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
        <Field label="Cost center">
          <select value={form.costCenter || ""} onChange={(e) => setForm({ ...form, costCenter: e.target.value || undefined })} data-testid="edit-cc" className="w-full border border-slate-300 rounded px-2 py-1 text-sm">
            <option value="">—</option>
            {dir.costCenters.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.code || c.id})</option>)}
          </select>
        </Field>
        <Field label="Direct manager">
          <select value={form.directManagerId || ""} onChange={(e) => setForm({ ...form, directManagerId: e.target.value || undefined })} data-testid="edit-mgr" className="w-full border border-slate-300 rounded px-2 py-1 text-sm">
            <option value="">—</option>
            {dir.managers.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.email} ({m.role})</option>)}
          </select>
        </Field>
        <Field label="Access start"><input type="date" value={(form.accessStartDate || "").slice(0, 10)} onChange={(e) => setForm({ ...form, accessStartDate: e.target.value })} data-testid="edit-start" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
        <Field label="Access expiry (optional)"><input type="date" value={(form.accessExpiryDate || "").slice(0, 10)} onChange={(e) => setForm({ ...form, accessExpiryDate: e.target.value })} data-testid="edit-expiry" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>

        {err && <div className="text-xs text-red-600" data-testid="edit-error">{err}</div>}

        <div className="flex gap-2 pt-2 border-t border-slate-200">
          <button onClick={save} disabled={busy} data-testid="edit-save" className="bg-slate-900 text-white text-sm rounded px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50">{busy ? "Saving…" : "Save changes"}</button>
          <button onClick={forceLogout} data-testid="edit-force-logout" className="text-red-700 border border-red-300 text-sm rounded px-3 py-1.5 hover:bg-red-50 inline-flex items-center gap-1.5"><LogOut size={13} /> Force logout</button>
        </div>

        {/* Sessions history */}
        {sessions.length > 0 && (
          <div className="pt-4 border-t border-slate-200" data-testid="edit-sessions-block">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Recent logins</div>
            <div className="space-y-1 text-xs">
              {sessions.slice(0, 8).map((s, i) => (
                <div key={i} className="flex items-center justify-between gap-2 border-b border-slate-100 pb-1">
                  <div>
                    <span className={s.success ? "text-emerald-700" : "text-red-700"}>{s.success ? "✓" : "✗"}</span>{" "}
                    <span className="text-slate-700">{s.method}</span> · {s.device} · {s.browser} · {s.ip || "—"} {s.country ? `(${s.country})` : ""}
                    {s.reason && s.reason !== "" && <span className="text-red-600 ml-1">[{s.reason}]</span>}
                  </div>
                  <div className="text-slate-500">{s.at ? new Date(s.at).toLocaleString() : ""}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InvitePanel({ dir, onClose, onSaved }: { dir: Directory; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<Partial<Row>>({
    email: "", name: "", role: "employee",
    ventureIds: ["orvan"], moduleAccess: [], approvalAuthority: [],
    status: "invited",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api("/api/pureh/admin/users-access", {
        method: "POST",
        body: JSON.stringify({
          email: (form.email || "").trim(),
          name: (form.name || "").trim(),
          role: form.role,
          ventureIds: form.ventureIds || [],
          department: form.department,
          team: form.team,
          jobTitle: form.jobTitle,
          moduleAccess: form.moduleAccess || [],
          approvalAuthority: form.approvalAuthority || [],
          approvalLimit: form.approvalLimit ?? null,
          costCenter: form.costCenter,
          directManagerId: form.directManagerId,
          accessStartDate: form.accessStartDate,
          accessExpiryDate: form.accessExpiryDate,
        }),
      });
      onSaved();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="users-invite-panel">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-[480px] bg-white border-l border-slate-200 h-full overflow-y-auto p-5 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Administration</div>
            <div className="text-lg font-semibold text-slate-900">Invite user</div>
          </div>
          <button onClick={onClose} data-testid="invite-close"><X size={16} /></button>
        </div>
        <Field label="Google email"><input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" placeholder="name@company.com" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
        <Field label="Full name"><input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
        <Field label="Role">
          <select value={form.role || "employee"} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="invite-role" className="w-full border border-slate-300 rounded px-2 py-1 text-sm">
            {dir.roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Ventures">
          <MultiSelect options={dir.ventures.map((v) => ({ value: v.id, label: v.name }))} value={form.ventureIds || []} onChange={(v) => setForm({ ...form, ventureIds: v })} testId="invite-ventures" />
        </Field>
        <Field label="Department">
          <select value={form.department || ""} onChange={(e) => setForm({ ...form, department: e.target.value || undefined })} data-testid="invite-dept" className="w-full border border-slate-300 rounded px-2 py-1 text-sm">
            <option value="">—</option>
            {dir.departments.map((d) => <option key={d.key || d.name} value={d.name}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="Job title"><input value={form.jobTitle || ""} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} data-testid="invite-title" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
        <Field label="Cost center">
          <select value={form.costCenter || ""} onChange={(e) => setForm({ ...form, costCenter: e.target.value || undefined })} data-testid="invite-cc" className="w-full border border-slate-300 rounded px-2 py-1 text-sm">
            <option value="">—</option>
            {dir.costCenters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Direct manager">
          <select value={form.directManagerId || ""} onChange={(e) => setForm({ ...form, directManagerId: e.target.value || undefined })} data-testid="invite-mgr" className="w-full border border-slate-300 rounded px-2 py-1 text-sm">
            <option value="">—</option>
            {dir.managers.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.email}</option>)}
          </select>
        </Field>
        <Field label="Modules">
          <MultiSelect options={MODULE_KEYS.map((k) => ({ value: k, label: k }))} value={form.moduleAccess || []} onChange={(v) => setForm({ ...form, moduleAccess: v })} testId="invite-modules" />
        </Field>
        {err && <div className="text-xs text-red-600" data-testid="invite-error">{err}</div>}
        <div className="flex gap-2 pt-2">
          <button onClick={submit} disabled={busy || !form.email || !form.name} data-testid="invite-submit" className="bg-slate-900 text-white text-sm rounded px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50">{busy ? "Inviting…" : "Send invitation"}</button>
          <button onClick={onClose} className="text-slate-600 text-sm">Cancel</button>
        </div>
        <div className="text-[10px] text-slate-500 mt-3">
          Invited users receive access when they sign in with the exact Google
          email above. Password fallback is disabled for invited users.
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function MultiSelect({ options, value, onChange, testId }: {
  options: { value: string; label: string }[];
  value: string[]; onChange: (v: string[]) => void; testId?: string;
}) {
  const toggle = (v: string) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };
  return (
    <div className="flex flex-wrap gap-1" data-testid={testId}>
      {options.map((o) => {
        const on = value.includes(o.value);
        return (
          <button
            key={o.value}
            onClick={() => toggle(o.value)}
            data-testid={`${testId}-${o.value}`}
            className={
              "text-[11px] px-2 py-0.5 rounded border transition-colors " +
              (on
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50")
            }
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
